'use client';

import { type FC, useMemo } from 'react';
import type { SelectOption } from '@/lib/types/select-option';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/lib/ui-kit/dropdown-menu';
import { Icon } from '@/lib/ui-kit/icons';
import { Text } from '@/lib/ui-kit/text';
import { cn } from '@/lib/ui-kit/utils';

interface CommentsSortProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  contentClassName?: string;
  triggerClassName?: string;
}

export const CommentsSort: FC<CommentsSortProps> = ({
  options,
  value,
  onChange,
  contentClassName = '',
  triggerClassName = '',
}) => {
  const selectedLabel = useMemo(() => {
    const option = options.find((o) => o.value === value);
    return option?.label ?? value ?? '';
  }, [options, value]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={triggerClassName}>
        <div className="cursor-pointer">
          <div className="flex items-center">
            <Text
              variant="body-small"
              className="flex items-center text-[13px] font-normal leading-4 tracking-[-0.01em] text-v2-text-primary"
            >
              {selectedLabel}
            </Text>
            <Icon name="ArrowSmallDown" size={24} className="text-v2-text-primary opacity-50" />
          </div>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        side="bottom"
        className={cn(
          contentClassName,
          'bg-v2-surface-secondary border-v2-border min-w-[140px] rounded-[10px] p-1',
        )}
      >
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex cursor-pointer items-center justify-between rounded-md px-3 py-2 transition-colors',
              'text-v2-text-primary hover:bg-v2-btn-secondary-hover focus:bg-v2-btn-secondary-hover focus:text-v2-text-primary',
            )}
          >
            <Text
              variant="body-small"
              className="text-[13px] font-normal leading-4 tracking-[-0.01em] text-v2-text-primary"
            >
              {option.label}
            </Text>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
